// React
import React, { useEffect, useState } from "react";
import s from "../AgencyModal/AgencyModal.module.css";
// Assets
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
// MaterialUi
import CircularProgress from "@mui/material/CircularProgress"
import Grid from "@mui/material/Grid"
import Modal from "@mui/material/Modal"
import TableContainer from "@mui/material/TableContainer"
import Table from "@mui/material/Table"
import TableHead from "@mui/material/TableHead"
import TableBody from "@mui/material/TableBody"
import TableRow from "@mui/material/TableRow"
import TableCell from "@mui/material/TableCell"
import Pagination from "@mui/material/Pagination"
import TextField from "@mui/material/TextField"
import Button from "@mui/material/Button"
import CloseIcon from "@mui/icons-material/Close";
// Services
import providersService from "../../../api/providers/providersService";
import couponsService from "../../../api/delivery/couponsService";
// Utils
import { useNotificationContext } from "../../Notifications/CustomNotifications";

const cellStyles = {
  fontFamily: "Roboto, sans-serif",
  fontSize: ".9rem",
  fontWeight: "400",
  borderBottom: "1px solid rgba(0, 0, 0, 0.455)",
  cursor: "pointer",
};

// =========================== | PROVIDERS MODAL | =============================

const ProvidersModal = ({ open, setOpen, selectProvider, type }) => {
  // Section: Utils Hooks
  const notification = useNotificationContext();
  const { t } = useTranslation();
  const { textColor } = useSelector((state) => state.local);
  const dispatch = useDispatch();

  // Section: States
  const [items, setItems] = useState([]);

  //paginado
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(0);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({
    increasing: true,
    name: "businessName",
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const handlePaginate = (e, v) => {
    setPage(v);
  };

  useEffect(() => {
    if (type === "Salida") {
      setLoading(true);
      couponsService
        .getSuppliersByStockItem(
          page,
          10,
          null,
          { razonsocial: query, cuit: 0 },
          sort,
        )
        .then((res) => {
          setItems(res.content);
          setTotalPages(res.totalPages);
        })
        .then(() => setLoading(false));
    } else {
      if (searching) {
        setLoading(true);
        providersService
          .searchProvider(page, 10, { razonsocial: query, cuit: 0 }, dispatch)
          .then((res) => {
            setItems(res.content);
            setTotalPages(res.totalPages);
          })
          .then(() => setLoading(false))
          .catch((error) => {
            notification.error("Error endpoint", error.message);
          });
      } else {
        setLoading(true);
        providersService
          .getProviders(page, 10, dispatch, sort)
          .then((res) => {
            setItems(res.content);
            setTotalPages(res.totalPages);
          })
          .then(() => setLoading(false))
          .catch((error) => {
            notification.error("Error endpoint", error.message);
          });
      }
    }
  }, [dispatch, notification, page, query, searching, sort, type]);

  // Section: JSX
  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <Grid container>
        <Grid
          xs={12}
          md={8}
          lg={4.5}
          item
          container
          className={s.boxModal}
          sx={{ bgcolor: "#EEEEEE" }}
        >
          <Grid
            container
            item
            xs={12}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Grid item xs={11} className={s.exit}>
              <div className={s.titleModal}>
                <p>{t("providers")}</p>
                <span>{t("select")}</span>
              </div>
              <CloseIcon
                onClick={() => setOpen(false)}
                className={s.exitIcon}
                sx={{ color: "primary.dark" }}
              />
            </Grid>

            <Grid
              item
              xs={11}
              sx={{
                mb: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              className={s.search}
            >
              <TextField
                label={t("find")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ width: "60%" }}
              />
              <Button
                variant="contained"
                disabled={query === ""}
                onClick={() => {
                  setPage(1);
                  setSearching((prev) => prev + 1);
                }}
              >
                {t("find")}
              </Button>
              <Button
                variant="contained"
                disabled={searching === 0}
                onClick={() => {
                  setPage(1);
                  setSearching(0);
                  setQuery("");
                }}
              >
                {t("clear")}
              </Button>
            </Grid>

            {loading ? (
              <Grid item xs={12} className={s.loader}>
                <CircularProgress />
              </Grid>
            ) : items?.length ? (
              <Grid item container xs={11} className={s.data}>
                <TableContainer className={s.tableContainer}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            "&:hover": { cursor: "pointer" },
                            backgroundColor: "primary.main",
                            color: textColor,
                            fontSize: ".9rem",
                            fontWeight: 500,
                            fontFamily: "Roboto, sans-Serif",
                          }}
                          onClick={() => {
                            setSort((prev) => ({
                              increasing: !prev.increasing,
                              name: "cuit",
                            }));
                          }}
                        >{`CUIT ${sort.name === "cuit" ? (sort.increasing ? "▼" : "▲") : ""}`}</TableCell>
                        <TableCell
                          sx={{
                            "&:hover": { cursor: "pointer" },
                            backgroundColor: "primary.main",
                            color: textColor,
                            fontSize: ".9rem",
                            fontWeight: 500,
                            fontFamily: "Roboto, sans-Serif",
                          }}
                          onClick={() => {
                            setSort((prev) => ({
                              increasing: !prev.increasing,
                              name: "businessName",
                            }));
                          }}
                        >{`${t("businessName")} ${sort.name === "businessName" ? (sort.increasing === "ASC" ? "▼" : "▲") : ""}`}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, i) => {
                        return (
                          item.enabled ? (
                          <React.Fragment key={item.id}>
                            <TableRow
                              sx={{
                                "&:hover": {
                                  backgroundColor: "secondary.light",
                                },
                              }}
                              onClick={() => {
                                selectProvider(item);
                                setOpen(false);
                              }}
                            >
                              <TableCell
                                sx={{
                                  ...cellStyles,
                                  backgroundColor:
                                    i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                                }}
                              >{`${item.cuit}`}</TableCell>
                              <TableCell
                                sx={{
                                  ...cellStyles,
                                  backgroundColor:
                                    i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                                }}
                              >
                                {item.businessName}
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                          ) : ( <></>)
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            ) : (
              <Grid item xs={12} className={s.noresult}>
                {t("noResults")}
              </Grid>
            )}
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "center", my: 5 }}
              className={s.pagination}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePaginate}
                color="primary"
                disabled={loading}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default ProvidersModal;
